'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from '@/i18n/routing';
import { FileSearch, Scale, ShieldCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';

/**
 * Product pitch for advocates, on the public advocate profile page (#11040).
 *
 * ## Why this sits here
 *
 * Search Console, 90 days to 2026-09-21: 13,462 clicks, and the query list is
 * almost entirely PERSONAL NAMES of individual advocates — a surname, or a
 * surname plus "advokat"/"kontakt". The brand query `lexomat` took 31 of those
 * clicks. So essentially all of our organic traffic lands on a profile page,
 * and a large share of it is the advocate looking themselves up.
 *
 * (The actual names are in the issue, not here: a source comment is the wrong
 * place to pin named individuals for the life of the file.)
 *
 * That visitor is our exact ICP arriving for free, and until this component
 * the page said nothing about the product — only "claim your free listing",
 * a DIRECTORY pitch. The AI product appeared nowhere but the footer tagline.
 *
 * ## Why it is self-identifying
 *
 * The same page also serves members of the public looking for a lawyer, and
 * nothing in the request distinguishes them from the advocate. Rather than
 * guess, this block opens with "Ste advokát?" and lets the reader self-select
 * — the pattern the claim banner above already uses ("Ste {name}?"), so it
 * reads as native rather than as an ad bolted on.
 *
 * ## Copy constraints — read before editing the strings
 *
 * Režim (c) of the SAK methodological guideline classifies the ADVOCATE'S USE
 * of an AI tool, and SAK does not certify tools. Marketing may state that our
 * čl. 28 GDPR DPA exists and meets the guideline's čl. 6 minimum requirements;
 * it must NEVER say the tool itself is certified, approved or compliant under
 * a SAK režim. Fenced by `advocate-product-pitch-honesty.test.ts`, which
 * mirrors the ratified fence on the advocate landing page (@citbaj 2026-07-05).
 *
 * The safest course, taken here, is not to mention SAK at all: the pitch
 * states what the tool does and that a čl. 28 DPA backs it, and stops there.
 */
export function AdvocateProductPitch() {
  const t = useTranslations('Lawyers.profile.productPitch');

  const points = [
    { icon: Scale, key: 'sources' as const },
    { icon: FileSearch, key: 'citations' as const },
    { icon: ShieldCheck, key: 'dpa' as const },
  ];

  return (
    <Card className='mt-6 border-brand-tertiary/40 bg-brand-tertiary/5'>
      <CardContent className='p-5 sm:p-6'>
        <p className='text-base font-semibold text-content-primary'>
          {t('title')}
        </p>
        <p className='mt-1 text-sm text-content-secondary max-w-[65ch]'>
          {t('description')}
        </p>

        <ul className='mt-4 grid gap-3 sm:grid-cols-3'>
          {points.map(({ icon: Icon, key }) => (
            <li key={key} className='flex items-start gap-2.5'>
              <Icon
                className='h-4 w-4 shrink-0 mt-0.5 text-brand-tertiary'
                aria-hidden='true'
              />
              <span className='text-sm text-content-secondary'>
                {t(`points.${key}`)}
              </span>
            </li>
          ))}
        </ul>

        <Button
          asChild
          size='sm'
          className='mt-5 bg-brand-tertiary hover:bg-brand-tertiary-hover text-white w-full sm:w-auto'
        >
          {/* Points at pricing, which is the only live surface today that
              explains the product and its price. Once the auth-aware root
              landing page (#11044) ships, retarget this at it — pricing
              answers "what does it cost", not "what is this". */}
          <Link href='/pricing'>{t('cta')}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
